// import { initializeSess// Принудительное уведомление для отладки (временно)
// if (import.meta.env.VITE_SENTRY_ENVIRONMENT === 'development') {
//   setTimeout(() => {
//     const versionInfo = getVersionInfo()
//     alert(`🔍 Sentry Debug ${versionInfo.formatted}:
// ENABLED: ${import.meta.env.VITE_SENTRY_ENABLED}
// DSN: ${import.meta.env.VITE_SENTRY_DSN ? 'SET' : 'NOT SET'}
// ENV: ${import.meta.env.VITE_SENTRY_ENVIRONMENT}
// Initialized: ${isSentryEnabled && sentryDsn}`)
//   }, 1000)
// }'./shared/lib/auth/sessionInitializer'
// Глобальные стили подключаются через Vite `additionalData` (@use index.scss)

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter } from "vue-router";
import App from './App.vue'
import router from './router'
import * as Sentry from "@sentry/vue";
import { getVersionInfo } from './shared/utils/version/getAppVersion';
import { initTelegramViewportHeightSync } from './shared/lib/telegram/viewport'
import { isTelegramSession } from './shared/lib/telegram/runtime'
import { registerSW } from 'virtual:pwa-register'

// Инициализация Telegram Web App
if (window.Telegram?.WebApp) {
  console.log('🔔 Initializing Telegram Web App...')
  window.Telegram.WebApp.ready()
  window.Telegram.WebApp.expand()

  // Синхронизируем высоту вьюпорта, чтобы контент занимал всю доступную высоту без скроллов
  initTelegramViewportHeightSync()

  // Добавляем класс платформы на html для адресных стилей под Windows/tdesktop
  try {
    const platform = window.Telegram.WebApp.platform
    if (platform) {
      document.documentElement.classList.add(`tg-platform-${platform}`)
    }
  } catch (_) { /* no-op */ }
  
  // Логируем информацию о доступных API
  console.log('🔔 Telegram Web App Info:', {
    platform: window.Telegram.WebApp.platform,
    hasHapticFeedback: !!window.Telegram.WebApp.HapticFeedback,
    colorScheme: window.Telegram.WebApp.colorScheme,
    isExpanded: window.Telegram.WebApp.isExpanded
  })
  
  if (window.Telegram.WebApp.HapticFeedback) {
    console.log('✅ HapticFeedback API доступен')
    // Тестовая вибрация для подтверждения работы
    try {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
      console.log('✅ Тестовая вибрация выполнена успешно')
    } catch (error) {
      console.error('❌ Ошибка при тестовой вибрации:', error)
    }
  } else {
    console.warn('❌ HapticFeedback API недоступен')
  }
} else {
  console.log('🌐 Запуск вне Telegram Web App')
  // Вне Telegram тоже выставляем корректную высоту
  initTelegramViewportHeightSync()
}

if ('serviceWorker' in navigator && !isTelegramSession()) {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      window.dispatchEvent(new CustomEvent('pwa:update-ready'))
    },
    onOfflineReady() {
      window.dispatchEvent(new CustomEvent('pwa:offline-ready'))
    },
    onRegisterError(error: unknown) {
      console.error('Не удалось зарегистрировать service worker:', error)
    },
  })

  window.addEventListener('pwa:apply-update', () => {
    updateSW(true)
  })
}


const app = createApp(App)
const pinia = createPinia()

// Инициализируем Sentry только если он включен
const isSentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'
const sentryDsn = import.meta.env.VITE_SENTRY_DSN

console.log('🔍 Sentry Debug Info:', {
  VITE_SENTRY_ENABLED: import.meta.env.VITE_SENTRY_ENABLED,
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN ? '✅ SET' : '❌ NOT SET',
  VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  isSentryEnabled,
  willInitialize: isSentryEnabled && sentryDsn
})

// Принудительное уведомление для отладки (временно)
// if (import.meta.env.VITE_SENTRY_ENVIRONMENT === 'development') {
//   setTimeout(() => {
//     const versionInfo = getVersionInfo()
//         alert(`🔍 Sentry Debug ${versionInfo.formatted}:
//     ENABLED: ${import.meta.env.VITE_SENTRY_ENABLED}
//     DSN: ${import.meta.env.VITE_SENTRY_DSN ? 'SET' : 'NOT SET'}
//     ENV: ${import.meta.env.VITE_SENTRY_ENVIRONMENT}
//     Initialized: ${isSentryEnabled && sentryDsn}`)
//       }, 1000)
// }

if (isSentryEnabled && sentryDsn) {
  console.log('🚀 Initializing Sentry...')
  
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'local_development',
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.podpiska\.vip\/api/],
    // Session Replay
    replaysSessionSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production' ? 0.01 : 0.1,
    replaysOnErrorSampleRate: 1.0,
  })

  console.log('✅ Sentry initialized successfully!')

  // Глобальная обработка ошибок Vue
  app.config.errorHandler = (err, vm, info) => {
    console.error('Vue error:', err, info);
    Sentry.captureException(err, {
      extra: {
        info,
        componentStack: vm?.$options?.name || 'Unknown component',
      },
    });
  };

  // Глобальная обработка необработанных промисов
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    Sentry.captureException(event.reason, {
      extra: {
        type: 'unhandledrejection',
        promise: event.promise,
      },
    });
  });

  // Отправляем тестовое сообщение для проверки
  setTimeout(() => {
    Sentry.addBreadcrumb({
      message: 'Приложение успешно запущено',
      category: 'app',
      level: 'info',
    });
    
    Sentry.captureMessage('Приложение VPN без проблем запущено успешно', 'info');
    console.log('📡 Test message sent to Sentry')
  }, 2000);
} else {
  console.log('❌ Sentry отключен для среды:', import.meta.env.VITE_SENTRY_ENVIRONMENT);
}

app.use(pinia)
app.use(router)

// Инициализируем сессию после создания Pinia
// initializeSession()

app.mount('#app')
